import type { MouseEvent } from 'react';
import { UsersThreeIcon } from '@phosphor-icons/react';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { useSendToSpace } from '@/hooks/useSendToSpace';
import { adjustableSpace, canSendTransaction, sentExpenseLabels } from '@/lib/spaces/sending';
import type { Transaction } from '@/types';
import type { SentExpense } from '@/types/spaces';

// The row carries the spaces it is in (`Transaction.spaceExpenses`, VER-571): the label names the one to adjust.
export function SpaceSendAction({ transaction }: { transaction?: Transaction }) {
  const sending = useSendToSpace();
  const { spaces } = useSpaceScope();
  if (!sending || !transaction || !canSendTransaction(transaction)) return null;
  const first = adjustableSpace(spaces, transaction.spaceExpenses);
  const label = first ? `${first.name} · ajustar` : 'Enviar para espaço';
  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => { event.stopPropagation(); sending.open(transaction); };
  return <button type="button" onClick={handleOpen} className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-xs font-medium text-text-primary hover:bg-surface-sunken focus-visible:outline-2 focus-visible:outline-ring">
    <UsersThreeIcon className="size-3" aria-hidden />{label}
  </button>;
}

export function SpaceTransactionMarker({ spaceExpenses }: { spaceExpenses?: readonly SentExpense[] | null }) {
  const sending = useSendToSpace();
  const { spaces } = useSpaceScope();
  if (!sending) return null;
  const labels = sentExpenseLabels(spaces, spaceExpenses);
  if (!labels.length) return null;
  return <span className="mt-1 flex items-center gap-1 text-xs text-text-secondary"><UsersThreeIcon className="size-3 shrink-0" aria-hidden /><span className="truncate">{labels.join(' · ')}</span></span>;
}

// A row that is not a transaction of the list (a fatura's charge): the drawer reads the transaction itself, with the
// spaces it is in, once opened; the label names one only where the row already carries them.
export function SpaceSendLookupAction({ transactionId, eligible, spaceExpenses }: { transactionId: string; eligible: boolean; spaceExpenses?: readonly SentExpense[] | null }) {
  const sending = useSendToSpace();
  const { spaces } = useSpaceScope();
  if (!sending || !eligible) return null;
  const existing = adjustableSpace(spaces, spaceExpenses);
  const label = existing ? `${existing.name} · ajustar` : 'Enviar para espaço';
  const handleOpen = () => { void sending.openById(transactionId); };
  return <button type="button" onClick={handleOpen} disabled={sending.resolving} aria-busy={sending.resolving} className="mt-1 inline-flex cursor-pointer items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-1 text-xs text-text-primary hover:bg-surface-sunken disabled:opacity-60"><UsersThreeIcon className="size-3" aria-hidden />{sending.resolving ? 'Abrindo…' : label}</button>;
}
